(function () {
  'use strict';

  const endpoint = '/apps/popwdata/wishlist';
  const savedVariants = new Set();
  let loaded = false;
  let loadingPromise = null;

  function buttons() {
    return Array.from(document.querySelectorAll('[data-wishlist-button]'));
  }

  function setButtonState(button, saved) {
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    button.classList.toggle('is-saved', saved);
    const label = button.querySelector('[data-wishlist-label]');
    if (label) label.textContent = saved ? 'Remove from wishlist' : 'Add to wishlist';
    const productName = button.closest('[data-product-id]')?.querySelector('.product-item__title, h3')?.textContent?.trim() || 'item';
    button.setAttribute('aria-label', `${saved ? 'Remove' : 'Add'} ${productName} ${saved ? 'from' : 'to'} wishlist`);
  }

  function syncButtons() {
    buttons().forEach(button => setButtonState(button, savedVariants.has(button.dataset.variantId)));
  }

  async function loadWishlist() {
    if (loaded) return;
    if (loadingPromise) return loadingPromise;
    loadingPromise = fetch(endpoint, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(async response => {
      if (response.status === 401) return { loginRequired: true, items: [] };
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || 'Unable to load wishlist.');
      (data.items || []).forEach(item => savedVariants.add(String(item.v)));
      loaded = true;
      syncButtons();
      document.dispatchEvent(new CustomEvent('wishlist:loaded', { detail: data }));
      return data;
    }).finally(() => {
      loadingPromise = null;
    });
    return loadingPromise;
  }

  async function toggle(button) {
    const loadedData = await loadWishlist();
    if (loadedData?.loginRequired) {
      const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `${window.routes?.accountLoginUrl || '/account/login'}?return_url=${returnTo}`;
      return;
    }
    const variantId = String(button.dataset.variantId || '');
    const wasSaved = savedVariants.has(variantId);
    button.disabled = true;
    button.classList.add('is-loading');
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          action: wasSaved ? 'remove' : 'add',
          product_id: button.dataset.productId,
          variant_id: variantId,
          handle: button.dataset.productHandle
        })
      });
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || 'Unable to update wishlist.');
      if (data.saved) savedVariants.add(variantId);
      else savedVariants.delete(variantId);
      syncButtons();
      document.dispatchEvent(new CustomEvent('wishlist:changed', {
        detail: { variantId, saved: !!data.saved, count: data.count }
      }));
      if (!data.saved) {
        const wishlistGrid = button.closest('[data-wishlist-grid]');
        const card = button.closest('.product-item');
        if (wishlistGrid && card) {
          card.remove();
          if (!wishlistGrid.querySelector('.product-item')) {
            const status = wishlistGrid.closest('[data-wishlist-page]')?.querySelector('[data-wishlist-status]');
            if (status) status.textContent = 'Your wishlist is empty.';
          }
        }
      }
    } catch (error) {
      window.alert(error.message || 'Unable to update wishlist.');
    } finally {
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('[data-wishlist-button]');
    if (button) {
      event.preventDefault();
      event.stopPropagation();
      toggle(button);
    }
  });

  document.addEventListener('variant:changed', event => {
    const variant = event.detail?.variant;
    const scope = event.target?.closest?.('[data-product-id]') || document;
    const button = scope.querySelector?.('[data-wishlist-button]');
    if (!button || !variant?.id) return;
    button.dataset.variantId = String(variant.id);
    setButtonState(button, savedVariants.has(String(variant.id)));
  });

  async function loadCardPage(root, page) {
    root.classList.add('is-loading');
    try {
      const response = await fetch(`${endpoint}/cards?page=${page}`, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      });
      if (!response.ok) throw new Error('Unable to load wishlist products.');
      const html = await response.text();
      const holder = document.createElement('div');
      holder.innerHTML = html;
      const meta = holder.querySelector('[data-wishlist-page-meta]');
      holder.querySelectorAll('.product-item').forEach(card => root.querySelector('[data-wishlist-grid]').appendChild(card));
      root.dataset.nextPage = meta?.dataset.nextPage || '';
      const more = root.querySelector('[data-wishlist-more]');
      more.hidden = !root.dataset.nextPage;
      await loadWishlist();
      syncButtons();
      const status = root.querySelector('[data-wishlist-status]');
      if (status && !root.querySelector('[data-wishlist-grid] .product-item') && !root.dataset.nextPage) {
        status.textContent = 'Your wishlist is empty.';
      } else if (status) {
        status.textContent = '';
      }
    } catch (error) {
      const status = root.querySelector('[data-wishlist-status]');
      status.textContent = error.message;
    } finally {
      root.classList.remove('is-loading');
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    if (buttons().length) loadWishlist().catch(() => {});
    document.querySelectorAll('[data-wishlist-page]').forEach(root => {
      loadCardPage(root, 1);
      root.querySelector('[data-wishlist-more]')?.addEventListener('click', () => {
        if (root.dataset.nextPage) loadCardPage(root, root.dataset.nextPage);
      });
    });

    let mutationSyncQueued = false;
    new MutationObserver(mutations => {
      if (mutationSyncQueued || !mutations.some(mutation =>
        Array.from(mutation.addedNodes).some(node =>
          node.nodeType === 1 && (node.matches?.('[data-wishlist-button]') || node.querySelector?.('[data-wishlist-button]'))
        )
      )) return;
      mutationSyncQueued = true;
      window.requestAnimationFrame(() => {
        mutationSyncQueued = false;
        loadWishlist().then(syncButtons).catch(() => {});
      });
    }).observe(document.body, { childList: true, subtree: true });
  });
})();
